define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../System/Camera',
    '../System/Vec3Obj',
    '../Enum/EnumPresentationItemType',
    '../Enum/EnumConditionType'
], function (
    defaultValue,
    defined,
    encode,
    Camera,
    Vec3Obj,
    EnumPresentationItemType,
    EnumConditionType
) {
    'use strict';

    /**
     * 演示子项对象。
     * 
     * @demo {@link http://lbs.zjkelan.com/PresentationItem.html|CooWasm PresentationItem demo}
     * @alias PresentationItem
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {EnumPresentationItemType} [props.type=EnumPresentationItemType.CAMERA] 子项类型。
     * @param {String} [props.name=""] 子项名称。
     * @param {EnumConditionType} [props.condition=EnumConditionType.IMMEDIATE] 执行动作条件类型。
     * @param {Number} [props.waitTime=0.0] 执行动作等待时间，单位：秒。
     * @param {Camera} [props.camera=new Camera()] 当EnumPresentationItemType为CAMERA时，所对应的Camera对象，如不传递，则自动根据当前视点信息记录。
     * @param {Array.<String>} [props.showUuids] 当EnumPresentationItemType为SHOW时，所对应绑定对象的avatarId的数组。不得为空数组。
     * @param {Array.<String>} [props.hideUuids] 当EnumPresentationItemType为HIDE时，所对应绑定对象的avatarId的数组。不得为空数组。
     * @param {String} [props.followUuid] 当EnumPresentationItemType为FOLLOW时，所对应绑定对象的avatarId。必须对应某个对象。
     * @param {Number} [props.distance] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的距离。
     * @param {Number} [props.azimuth] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的水平旋转角。
     * @param {Number} [props.pitch] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的垂直俯仰角。
     * @param {Number} [props.liftUp] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的抬高高度。
     * @param {Vec3Obj} [props.aimPosition] 当EnumPresentationItemType为FOLLOW时，相机所朝向的目标经纬度点。
     * @param {Function} [props.func] 当子项播放完成时触发的函数回调。
     * @param {CallbackBoolean} [callback=null] 创建成功后触发的函数回调。
     * @example
     * var item = new CooWasm.PresentationItem({},(state)=>{console.log("load status:" + state)});
     */
    class PresentationItem {
        /**
         * @description 子项类型。
         * @type {EnumPresentationItemType}
         */
        type;
        /**
         * @description 子项名称。
         * @type {String}
         */
        name;
        /**
         * @description 执行动作条件类型。
         * @type {EnumConditionType}
         */
        condition;
        /**
         * @description 执行动作等待时间，单位：秒。
         * @type {Number}
         */
        waitTime;
        /**
         * @description 当EnumPresentationItemType为CAMERA时，所对应的Camera对象，如不传递，则自动根据当前视点信息记录。
         * @type {Camera}
         */
        camera;
        /**
         * @description 当EnumPresentationItemType为SHOW时，所对应绑定对象的avatarId的数组。不得为空数组。
         * @type {Array.<String>}
         */
        showUuids;
        /**
         * @description 当EnumPresentationItemType为HIDE时，所对应绑定对象的avatarId的数组。不得为空数组。
         * @type {Array.<String>}
         */
        hideUuids;
        /**
         * @description 当EnumPresentationItemType为FOLLOW时，所对应绑定对象的avatarId。必须对应某个对象。
         * @type {String}
         */
        followUuid;
        /**
         * @description 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的距离。
         * @type {Number}
         */
        distance;
        /**
         * @description 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的水平旋转角。
         * @type {Number}
         */
        azimuth;
        /**
         * @description 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的垂直俯仰角。
         * @type {Number}
         */
        pitch;
        /**
         * @description 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的抬高高度。
         * @type {Number}
         */
        liftUp;
        /**
         * @description 当EnumPresentationItemType为FOLLOW时，相机所朝向的目标经纬度点。
         * @type {Vec3Obj}
         */
        aimPosition;
        /**
         * @description 当播放完成时触发的事件回调。
         * @type {String}
         */
        func;
        /**
         * @description 子项内部id。
         * @type {String}
         * @private
         */
        uuid;
        /**
         * @description PresentationItem在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "PresentationItem";

        constructor(props, callback) {
            if (!defined(props)) {
                return null;
            }
            this.type = defaultValue(props.type, EnumPresentationItemType.CAMERA);
            this.name = defaultValue(props.name, "");
            this.condition = defaultValue(props.condition, EnumConditionType.IMMEDIATE);
            this.waitTime = defaultValue(props.waitTime, 0.0);
            this.func = defaultValue(props.func, null);
            if (this.type == EnumPresentationItemType.CAMERA) {
                this.camera = defaultValue(props.camera, new Camera({bGet:true}, state=>{
                    if(defined(callback)){
                        callback(true);
                    }
                }));
            } else if (this.type == EnumPresentationItemType.SHOW) {
                if (!defined(props.showUuids) || props.showUuids.length === 0) {
                    console.log("showUuids is null!");
                    if (defined(callback)) {
                        callback(false);
                    }
                    return null;
                }
                this.showUuids = props.showUuids;
                if (defined(callback)) {
                    callback(true);
                }
            } else if (this.type == EnumPresentationItemType.HIDE) {
                if (!defined(props.hideUuids) || props.hideUuids.length === 0) {
                    console.log("hideUuids is null!");
                    if (defined(callback)) {
                        callback(false);
                    }
                    return null;
                }
                this.hideUuids = props.hideUuids;
                if (defined(callback)) {
                    callback(true);
                }
            } else if (this.type == EnumPresentationItemType.FOLLOW) {
                if (!defined(props.followUuid)) {
                    console.log("followUuid is null!");
                    if (defined(callback)) {
                        callback(false);
                    }
                    return null;
                }
                this.followUuid = props.followUuid;
                this.distance = defaultValue(props.distance, 10.0);
                this.azimuth = defaultValue(props.azimuth, 0.0);
                this.pitch = defaultValue(props.pitch, 0.0);
                this.liftUp = defaultValue(props.liftUp, 0.0);
                this.aimPosition = defaultValue(props.aimPosition, null);
                if (defined(callback)) {
                    callback(true);
                }
            }
        }
        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} props 包含如下属性的Object：
         * @param {EnumPresentationItemType} [props.type] 子项类型。
         * @param {String} [props.name] 子项名称。
         * @param {EnumConditionType} [props.condition] 执行动作条件类型。
         * @param {Number} [props.waitTime] 执行动作等待时间，单位：秒。
         * @param {Camera} [props.camera] 当EnumPresentationItemType为CAMERA时，所对应的Camera对象，如不传递，则自动根据当前视点信息记录。
         * @param {Array.<String>} [props.showUuids] 当EnumPresentationItemType为SHOW时，所对应绑定对象的avatarId的数组。不得为空数组。
         * @param {Array.<String>} [props.hideUuids] 当EnumPresentationItemType为HIDE时，所对应绑定对象的avatarId的数组。不得为空数组。
         * @param {String} [props.followUuid] 当EnumPresentationItemType为FOLLOW时，所对应绑定对象的avatarId。必须对应某个对象。
         * @param {Number} [props.distance] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的距离。
         * @param {Number} [props.azimuth] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的水平旋转角。
         * @param {Number} [props.pitch] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的垂直俯仰角。
         * @param {Number} [props.liftUp] 当EnumPresentationItemType为FOLLOW时，相机相对跟踪对象的抬高高度。
         * @param {Vec3Obj} [props.aimPosition] 当EnumPresentationItemType为FOLLOW时，相机所朝向的目标经纬度点。
         * @param {Function} [props.func] 当子项播放完成时触发的函数回调。
         * @example
         * item.update({
         *    name:"test"
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props) {
            this.type = defaultValue(props.type, this.type);
            this.name = defaultValue(props.name, this.name);
            this.condition = defaultValue(props.condition, this.condition);
            this.waitTime = defaultValue(props.waitTime, this.waitTime);
            this.camera = defaultValue(props.camera, this.camera);
            this.showUuids = defaultValue(props.showUuids, this.showUuids);
            this.hideUuids = defaultValue(props.hideUuids, this.hideUuids);

            this.followUuid = defaultValue(props.followUuid, this.followUuid);
            this.distance = defaultValue(props.distance, this.distance);
            this.azimuth = defaultValue(props.azimuth, this.azimuth);
            this.pitch = defaultValue(props.pitch, this.pitch);
            this.liftUp = defaultValue(props.liftUp, this.liftUp);
            this.aimPosition = defaultValue(props.aimPosition, this.aimPosition);

            this.func = defaultValue(props.func, this.func);
        }
        /**
         * 将PresentationItem实例转换成json格式的字符串。
         *
         * @returns {String} json格式的字符串。
         */
        toStr() {
            let jsonObj = {};
            jsonObj.ItemType = this.type;
            jsonObj.Name = this.name;
            jsonObj.ConditionType = this.condition;
            jsonObj.WaitSeconds = this.waitTime;
            if (this.type == EnumPresentationItemType.CAMERA) {
                jsonObj.FocalPoint = this.camera.focalPoint.toStr();
                jsonObj.Azimuth = Number(this.camera.azimuth);
                jsonObj.Pitch = Number(this.camera.pitch);
                jsonObj.Distance = Number(this.camera.distance);
                jsonObj.Duration = Number(this.camera.duration);
            } else if (this.type == EnumPresentationItemType.SHOW) {
                let str = "";
                this.showUuids.forEach(ele => {
                    str = str + ele + " ";
                });
                str = str.slice(0, -1);
                jsonObj.ShowAvatars = str;
            } else if (this.type == EnumPresentationItemType.HIDE) {
                let str = "";
                this.hideUuids.forEach(ele => {
                    str = str + ele + " ";
                });
                str = str.slice(0, -1);
                jsonObj.HideAvatars = str;
            } else if (this.type == EnumPresentationItemType.FOLLOW) {
                jsonObj.FollowAvatar = this.followUuid;
                jsonObj.Distance = Number(this.distance);
                jsonObj.Azimuth = Number(this.azimuth);
                jsonObj.Pitch = Number(this.pitch);
                jsonObj.LiftUp = Number(this.liftUp);
                jsonObj.ViewMode = 0;
                if(defined(this.aimPosition)){
                    jsonObj.FocalPoint = this.aimPosition.toStr();
                    jsonObj.ViewMode = 1;
                }
            }
            return JSON.stringify(jsonObj);
        }

        /**
         * 从场景中销毁自身。
         * @returns null 空值。
         * @example
         * // 销毁对象
         * item = item.destroy();
         */
        destroy() {
            this.type = null;
            this.name = "";
            this.condition = null;
            this.waitTime = 0.0;
            return null;
        }
    }
    return PresentationItem;
});